import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Search, DollarSign, Calendar, GraduationCap, AlertCircle, 
  CheckCircle, Play, Users, RefreshCw, Zap, ArrowRight, Filter, Eye 
} from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function DnaPayGerarTitulos() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [coursePlans, setCoursePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [turmaFilter, setTurmaFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [turmas, setTurmas] = useState([]);
  
  // Estados para geração
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [mesInicio, setMesInicio] = useState(1);
  const [mesFim, setMesFim] = useState(12);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultDialog, setResultDialog] = useState(false);
  const [resultado, setResultado] = useState(null);

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, turmaFilter, statusFilter, students]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, plansRes, turmasRes] = await Promise.all([
        api.get('/students'),
        api.get('/course-financial-plans'),
        api.get('/class-groups')
      ]);
      
      const activeStudents = (studentsRes.data || []).filter(s => s.active !== false && s.situation === 'Ativo');
      setStudents(activeStudents);
      setCoursePlans(plansRes.data || []);
      setTurmas(turmasRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];
    
    if (searchTerm) {
      filtered = filtered.filter(student => 
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.registration_number?.includes(searchTerm)
      );
    }
    
    if (turmaFilter !== 'all') {
      filtered = filtered.filter(s => s.class_group_id === turmaFilter);
    }
    
    if (statusFilter === 'com_plano') {
      filtered = filtered.filter(s => s.financial_plans?.length > 0 || s.financial_plan_id);
    } else if (statusFilter === 'sem_plano') {
      filtered = filtered.filter(s => !s.financial_plans?.length && !s.financial_plan_id);
    }
    
    setFilteredStudents(filtered);
  };

  const getStudentPlanInfo = (student) => {
    const plans = student.financial_plans || [];
    if (plans.length === 0 && !student.financial_plan_id) return null;
    
    let totalMensal = 0;
    const planNames = [];
    
    plans.forEach(planId => {
      const plan = coursePlans.find(p => p.id === planId);
      if (plan) {
        totalMensal += plan.installment_value || 0;
        planNames.push(plan.name);
      }
    });
    
    return { totalMensal, planNames, count: plans.length };
  };

  const handleSelectStudent = (studentId, checked) => {
    if (checked) {
      setSelectedStudents([...selectedStudents, studentId]);
    } else {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const studentsWithPlan = filteredStudents
        .filter(s => getStudentPlanInfo(s))
        .map(s => s.id);
      setSelectedStudents(studentsWithPlan);
    } else {
      setSelectedStudents([]);
    }
  };

  const handleGerarTitulos = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Selecione pelo menos um aluno');
      return;
    }

    const confirmMsg = `Gerar títulos para ${selectedStudents.length} aluno(s) de ${meses.find(m => m.value === mesInicio)?.label} a ${meses.find(m => m.value === mesFim)?.label}/${anoSelecionado}?`;
    if (!window.confirm(confirmMsg)) return;

    setGenerating(true);
    setProgress(0);

    const resultados = {
      sucesso: 0,
      erros: 0,
      detalhes: []
    };

    const total = selectedStudents.length;

    for (let i = 0; i < selectedStudents.length; i++) {
      const studentId = selectedStudents[i];
      const student = students.find(s => s.id === studentId);
      const planInfo = getStudentPlanInfo(student);

      if (!planInfo || planInfo.totalMensal <= 0) {
        resultados.erros++;
        resultados.detalhes.push({
          aluno: student?.name,
          status: 'erro',
          mensagem: 'Sem plano financeiro configurado'
        });
        continue;
      }

      try {
        // Gerar títulos para cada mês selecionado
        for (let mes = mesInicio; mes <= mesFim; mes++) {
          const competencia = `${anoSelecionado}-${String(mes).padStart(2, '0')}`;
          const vencimento = `${anoSelecionado}-${String(mes).padStart(2, '0')}-10`;
          
          // Criar descrição baseada nos planos
          const descricao = planInfo.planNames.length > 0 
            ? `${planInfo.planNames.join(' + ')} - ${meses.find(m => m.value === mes)?.label}/${anoSelecionado}`
            : `Mensalidade ${meses.find(m => m.value === mes)?.label}/${anoSelecionado}`;

          await api.post('/dnapay/billing/titulos', {
            aluno_id: studentId,
            responsavel_id: student.financial_responsible_id,
            descricao: descricao,
            categoria: 'mensalidade',
            competencia: competencia,
            valor_original: planInfo.totalMensal,
            desconto: 0,
            vencimento: vencimento,
            parcela: mes,
            total_parcelas: 12
          });
        }

        resultados.sucesso++;
        resultados.detalhes.push({
          aluno: student?.name,
          status: 'sucesso',
          mensagem: `${mesFim - mesInicio + 1} títulos gerados`
        });
      } catch (error) {
        resultados.erros++;
        resultados.detalhes.push({
          aluno: student?.name,
          status: 'erro',
          mensagem: error.response?.data?.detail || 'Erro ao gerar'
        });
      }

      setProgress(Math.round(((i + 1) / total) * 100));
    }

    setGenerating(false);
    setResultado(resultados);
    setResultDialog(true);
    setSelectedStudents([]);
    setSelectAll(false);

    if (resultados.sucesso > 0) {
      toast.success(`${resultados.sucesso} aluno(s) processados com sucesso!`);
    }
  };

  const studentsWithPlan = filteredStudents.filter(s => getStudentPlanInfo(s)).length;
  const totalValorMensal = filteredStudents.reduce((sum, s) => {
    const info = getStudentPlanInfo(s);
    return sum + (info?.totalMensal || 0);
  }, 0);

  return (
    <div className="p-6 space-y-6" data-testid="dnapay-gerar-titulos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-emerald-600" />
            Gerar Títulos DNA Pay
          </h1>
          <p className="text-muted-foreground mt-1">
            Gere títulos em massa a partir dos planos financeiros dos alunos
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Alunos Ativos</p>
                <p className="text-2xl font-bold">{students.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Com Plano</p>
                <p className="text-2xl font-bold">{studentsWithPlan}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selecionados</p>
                <p className="text-2xl font-bold">{selectedStudents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Receita Mensal</p>
                <p className="text-2xl font-bold">{formatCurrency(totalValorMensal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuração de Geração */}
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            Configurar Geração
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <Label>Ano</Label>
              <Select value={anoSelecionado.toString()} onValueChange={(v) => setAnoSelecionado(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(ano => (
                    <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mês Início</Label>
              <Select value={mesInicio.toString()} onValueChange={(v) => setMesInicio(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map(mes => (
                    <SelectItem key={mes.value} value={mes.value.toString()}>{mes.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mês Fim</Label>
              <Select value={mesFim.toString()} onValueChange={(v) => setMesFim(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map(mes => (
                    <SelectItem key={mes.value} value={mes.value.toString()}>{mes.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Button 
                onClick={handleGerarTitulos} 
                disabled={generating || selectedStudents.length === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Gerando... {progress}%
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Gerar Títulos ({selectedStudents.length} alunos)
                  </>
                )}
              </Button>
            </div>
          </div>
          {generating && (
            <div className="mt-4">
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Buscar aluno..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={turmaFilter} onValueChange={setTurmaFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todas as turmas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as turmas</SelectItem>
                {turmas.map(turma => (
                  <SelectItem key={turma.id} value={turma.id}>{turma.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status do plano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="com_plano">Com plano</SelectItem>
                <SelectItem value="sem_plano">Sem plano</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="selectAll"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <Label htmlFor="selectAll" className="cursor-pointer">
                Selecionar todos com plano ({studentsWithPlan})
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alunos */}
      <Card>
        <CardHeader>
          <CardTitle>Alunos</CardTitle>
          <CardDescription>
            {filteredStudents.length} alunos encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : filteredStudents.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum aluno encontrado com os filtros aplicados.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto dna-responsive-table sticky-first-col">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Matrícula</TableHead>
                    <TableHead>Turma</TableHead>
                    <TableHead>Planos</TableHead>
                    <TableHead>Valor Mensal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.slice(0, 100).map((student) => {
                    const planInfo = getStudentPlanInfo(student);
                    const isSelected = selectedStudents.includes(student.id);
                    
                    return (
                      <TableRow 
                        key={student.id} 
                        className={isSelected ? 'bg-emerald-50' : ''}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectStudent(student.id, checked)}
                            disabled={!planInfo}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {student.registration_number}
                        </TableCell>
                        <TableCell>{student.class_name}</TableCell>
                        <TableCell>
                          {planInfo ? (
                            <div>
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                {planInfo.count} plano(s)
                              </Badge>
                              {planInfo.planNames.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]">
                                  {planInfo.planNames.join(', ')}
                                </p>
                              )}
                            </div>
                          ) : (
                            <Badge variant="secondary">Sem plano</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {planInfo ? (
                            <span className="font-medium text-green-600">
                              {formatCurrency(planInfo.totalMensal)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {filteredStudents.length > 100 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Mostrando 100 de {filteredStudents.length} alunos. Use os filtros para refinar.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Resultado */}
      <Dialog open={resultDialog} onOpenChange={setResultDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Resultado da Geração
            </DialogTitle>
          </DialogHeader>
          
          {resultado && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{resultado.sucesso}</p>
                    <p className="text-sm text-green-700">Processados com sucesso</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-red-600">{resultado.erros}</p>
                    <p className="text-sm text-red-700">Erros</p>
                  </CardContent>
                </Card>
              </div>

              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aluno</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Detalhe</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultado.detalhes.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.aluno}</TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'sucesso' ? 'default' : 'destructive'}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.mensagem}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setResultDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
